import React, { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { Decision, NiwtAgent } from '../types';
import DecisionCard from './DecisionCard';
import RejectRecommendationModal from './RejectRecommendationModal';
import ReceiptsDrawer from './ReceiptsDrawer';

interface Props {
  decisions: Decision[];
  agents: NiwtAgent[];
}

const NextDecisionsList: React.FC<Props> = ({ decisions, agents }) => {
  const [rejectTarget, setRejectTarget] = useState<Decision | null>(null);
  const [receiptsTarget, setReceiptsTarget] = useState<Decision | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const visibleDecisions = decisions.filter((d) => !dismissedIds.has(d.id));

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-niwt-primary" />
        <h2 className="text-sm font-semibold text-gray-800">
          Next Decisions ({visibleDecisions.length})
        </h2>
      </div>

      {visibleDecisions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <Lightbulb className="w-8 h-8 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No pending decisions — you're all caught up.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleDecisions.map((decision) => (
            <DecisionCard
              key={decision.id}
              decision={decision}
              agents={agents}
              onReject={setRejectTarget}
              onOpenReceipts={setReceiptsTarget}
            />
          ))}
        </div>
      )}

      {rejectTarget && (
        <RejectRecommendationModal
          decision={rejectTarget}
          agents={agents}
          onClose={() => setRejectTarget(null)}
          onRejected={(id) => setDismissedIds((prev) => { const next = new Set(prev); next.add(id); return next; })}
        />
      )}

      {receiptsTarget && (
        <ReceiptsDrawer
          receipts={receiptsTarget.receipts}
          decisionTitle={receiptsTarget.title}
          onClose={() => setReceiptsTarget(null)}
        />
      )}
    </section>
  );
};

export default NextDecisionsList;
